import { BenchmarkMetric } from "./BenchmarkMetric";

function ResultsTable({
  caption,
  headers,
  rows,
}: {
  caption: string;
  headers: string[];
  rows: string[][];
}) {
  return (
    <div
      className="table-wrap threshold-table"
      role="region"
      aria-label={caption}
      tabIndex={0}
    >
      <table>
        <caption>{caption}</caption>
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[0]}>
              <th scope="row">{row[0]}</th>
              {row.slice(1).map((cell, index) => (
                <td key={index}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const fprHeaders = [
  "Collection",
  "Human texts",
  "Flagged · 0.01% target",
  "False-positive rate · 0.01% target",
  "Flagged · 0.1% target",
  "False-positive rate · 0.1% target",
];

const eslRows = [
  ["TOEFL11", "13,175", "0", "0%", "0", "0%"],
  ["ICNALE written essays", "5,398", "1", "0.019%", "1", "0.019%"],
  ["TOEFL91", "91", "2", "2.20%", "2", "2.20%"],
  ["All ESL essays", "18,664", "3", "0.016%", "3", "0.016%"],
];

const speechRows = [
  ["ICNALE spoken monologues", "4,358", "1", "0.023%", "2", "0.046%"],
  ["ICNALE spoken dialogues", "4,199", "0", "0%", "0", "0%"],
  ["All transcripts", "8,557", "1", "0.012%", "2", "0.023%"],
];

export function StylomeLargeEslReport() {
  return (
    <details className="benchmark-report">
      <summary>
        <span className="benchmark-identity">
          <span className="metadata-line">
            Stylome 1.2 Large · TOEFL11, ICNALE, TOEFL91
          </span>
          <strong>ESL writing</strong>
          <span>
            Essays written by people learning English as an additional language,
            from three independent collections.
          </span>
        </span>
        <span
          className="benchmark-summary-metrics"
          aria-label="Stylome 1.2 Large ESL writing highlights"
        >
          <BenchmarkMetric
            label="False-positive rate · 0.01% FPR target"
            value="0.016%"
            context="3 of 18,664 human essays flagged"
          />
        </span>
        <span className="benchmark-toggle">Full results</span>
      </summary>
      <div className="benchmark-body">
        <p className="benchmark-context">
          Stylome 1.2 Large correctly cleared 18,661 of 18,664 essays by
          non-native English writers, with the same result at both fixed
          operating points. Before scoring, every essay was checked against our
          training data and reference bank; none appeared in either. The
          thresholds were fixed before scoring.
        </p>
        <ResultsTable
          caption="Stylome 1.2 Large on ESL writing"
          headers={fprHeaders}
          rows={eslRows}
        />
        <p>
          <a
            href="https://catalog.ldc.upenn.edu/LDC2014T06"
            target="_blank"
            rel="noreferrer"
          >
            TOEFL11
          </a>{" "}
          contains real TOEFL exam essays from writers with 11 different native
          languages. The{" "}
          <a
            href="https://language.sakura.ne.jp/icnale/"
            target="_blank"
            rel="noreferrer"
          >
            ICNALE
          </a>{" "}
          essays were written by university students in 10 Asian countries and
          regions between 2007 and 2013, plus a United Arab Emirates module.
          TOEFL91 is the 91-essay panel from{" "}
          <a
            href="https://pmc.ncbi.nlm.nih.gov/articles/PMC10382961/"
            target="_blank"
            rel="noreferrer"
          >
            Liang et al. (2023)
          </a>
          , in which seven earlier GPT detectors labeled an average of 61.3% of
          the essays as AI-generated. Stylome 1.2 Large flagged 2 of the 91. The
          panel is small, so each essay changes its rate by about 1.1 percentage
          points.
        </p>
        <p>
          <strong>Native-speaker comparison.</strong> ICNALE also includes 400
          essays by native English speakers, written to the same two prompts
          under the same conditions. Stylome 1.2 Large flagged 0 of them at the
          0.01% target and 2 at the 0.1% target, compared with 1 of 5,398
          learner essays at both targets. We see no sign that ESL writing is
          flagged more often than native writing.
        </p>
        <p>
          <strong>Known limitation: professionally proofread essays.</strong>{" "}
          ICNALE also contains 795 learner essays revised by professional
          proofreaders. Stylome 1.2 Large labeled 32 of them (4.0%) as AI-edited
          at the 0.01% target, and 36 (4.5%) at the 0.1% target. We count these
          as human-written, so they are false positives. Heavy human editing of
          learner writing can resemble AI editing to the model.
        </p>
        <p className="fine evaluation-note">
          TOEFL11 and ICNALE evaluated October 19, 2026; TOEFL91 re-evaluated
          October 20, 2026, with the current reference bank (unchanged: 2 of
          91). The TOEFL11 copy is the 13,200-essay NLI Shared Task 2017
          release; 25 essays under the 200-character minimum were not scored.
          ICNALE uses the Written Essays 2.6 and UAE 1.0 learner essays; the
          Written Essays Plus module was excluded because it was collected after
          2022.
        </p>
      </div>
    </details>
  );
}

export function StylomeLargeSpeechReport() {
  return (
    <details className="benchmark-report">
      <summary>
        <span className="benchmark-identity">
          <span className="metadata-line">
            Stylome 1.2 Large · ICNALE Spoken
          </span>
          <strong>Transcribed speech</strong>
          <span>
            Transcripts of spoken English by learners and native speakers:
            one-minute monologues and interview answers.
          </span>
        </span>
        <span
          className="benchmark-summary-metrics"
          aria-label="Stylome 1.2 Large transcribed speech highlights"
        >
          <BenchmarkMetric
            label="False-positive rate · 0.01% FPR target"
            value="0.012%"
            context="1 of 8,557 human transcripts flagged"
          />
        </span>
        <span className="benchmark-toggle">Full results</span>
      </summary>
      <div className="benchmark-body">
        <p className="benchmark-context">
          Stylome 1.2 Large correctly cleared 8,556 of 8,557 transcripts at the
          0.01% target and 8,555 at the 0.1% target. Transcribed speech is rare
          in our training data, so this tests how the model handles an
          unfamiliar kind of text. None of the transcripts appeared in our
          training data or reference bank.
        </p>
        <ResultsTable
          caption="Stylome 1.2 Large on transcribed speech"
          headers={fprHeaders}
          rows={speechRows}
        />
        <p>
          The monologues were recorded by phone around 2013–2014. The dialogues
          are the interviewee&apos;s answers from interviews recorded around
          2017–2019; the interviewer&apos;s questions are not included. Both
          come from the{" "}
          <a
            href="https://language.sakura.ne.jp/icnale/"
            target="_blank"
            rel="noreferrer"
          >
            ICNALE
          </a>{" "}
          project.
        </p>
        <p className="fine evaluation-note">
          Evaluated October 19, 2026. Transcripts under the 200-character
          minimum were not scored (37 monologues, 36 dialogues). Each speaker
          contributes several transcripts.
        </p>
      </div>
    </details>
  );
}
